import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { isPanelHost, isPanelPath, isLegacyAccPath, panelRedirect } from "./lib/panelHost";
import "@/App.css";
import { SmoothScroll } from "./components/SmoothScroll";
import { Layout } from "./components/Layout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { LayoutProvider } from "./context/LayoutContext";

import Home from "./pages/Home";
import About from "./pages/About";
import InvestmentModel from "./pages/InvestmentModel";
import Projects from "./pages/Projects";
import Portfolio from "./pages/Portfolio";
import ProjectDetail from "./pages/ProjectDetail";
import Regions from "./pages/Regions";
import RegionDetail from "./pages/RegionDetail";
import Packages from "./pages/Packages";
import AgriculturalModel from "./pages/AgriculturalModel";
import VillaConstruction from "./pages/VillaConstruction";
import AgriculturalInfrastructure from "./pages/AgriculturalInfrastructure";
import WhyUsPage from "./pages/WhyUsPage";
import Media from "./pages/Media";
import MediaDetail from "./pages/MediaDetail";

import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import AdminLogin from "./pages/admin/AdminLogin";

// The panel portal is split out of the public bundle: a visitor to the site never downloads the
// accounting or investor code.
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const PortalApp = lazy(() => import("./pages/portal/PortalApp"));

const Spinner = () => (
  <div className="min-h-screen bg-lightgray flex items-center justify-center">
    <Loader2 className="w-6 h-6 text-green/50 animate-spin" />
  </div>
);

// The portal on its own host or on /app: never indexed, and sent to its dedicated host when one is configured.
const Portal = ({ redirectTo }) => {
  useEffect(() => {
    if (redirectTo) { window.location.replace(redirectTo); return undefined; }
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, [redirectTo]);
  if (redirectTo) return <Spinner />;
  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        <Route path="*" element={<PortalApp />} />
      </Routes>
    </Suspense>
  );
};

const SiteRoutes = () => (
  <SmoothScroll>
    <LayoutProvider>
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/investment-model" element={<InvestmentModel />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/projects/:slug" element={<ProjectDetail />} />
        <Route path="/regions" element={<Regions />} />
        <Route path="/regions/:slug" element={<RegionDetail />} />
        <Route path="/packages" element={<Packages />} />
        <Route path="/villa-construction" element={<VillaConstruction />} />
        <Route path="/agricultural-infrastructure" element={<AgriculturalInfrastructure />} />
        <Route path="/agricultural-model" element={<AgriculturalModel />} />
        <Route path="/why-smart-farmland" element={<WhyUsPage />} />
        <Route path="/media" element={<Media />} />
        <Route path="/media/:slug" element={<MediaDetail />} />
        <Route path="/news" element={<Navigate to="/media" replace />} />
        <Route path="/news/:slug" element={<Navigate to="/media" replace />} />
        <Route path="/gallery" element={<Navigate to="/media" replace />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/contact" element={<Contact />} />
      </Routes>
    </Layout>
    </LayoutProvider>
  </SmoothScroll>
);

function AppRoutes() {
  const location = useLocation();
  // Own host (app.<domain>): the portal answers on every path of that host.
  if (isPanelHost()) return <Portal />;
  // /muhasebe was the accounting panel's address; it now lives in the portal.
  if (isLegacyAccPath(location.pathname)) return <Navigate to={`/app${location.search}${location.hash}`} replace />;
  // /app on the main site: kept for previews and local development, redirected to the dedicated
  // host in production when REACT_APP_PANEL_URL is set.
  if (isPanelPath(location.pathname)) return <Portal redirectTo={panelRedirect(location.search, location.hash)} />;
  if (location.pathname.startsWith("/admin")) {
    return (
      <Suspense fallback={<Spinner />}>
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        </Routes>
      </Suspense>
    );
  }
  return <SiteRoutes />;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </div>
  );
}
